/**
 * The supplier portal client (api-server Phase 4).
 *
 * `supplierPortal.api.ts` exports it as `supplierPortalApi`. The wire shapes
 * are api-server `supplier-portal.types.ts`.
 *
 * Two roots, one per scope:
 *   supplier → `/supplier-portal/…`                    (scoped by the caller's session)
 *   internal → `/production/suppliers/:supplierId/board/…` (staff auth, internal payload)
 * A supplier token can therefore never reach the internal payload by guessing
 * a path; the server enforces scope and redaction on every endpoint.
 */
import { apiDelete, apiGet, apiPatch, apiPost, apiPostForm, apiPut } from "@/lib/apiClient";
import type { QueryParams } from "@/lib/apiClient";
import type { SupplierStatus } from "@/modules/SupplierOrders";

import { serializeFilters } from "../lib/boardFilters.ts";
import { bootstrapParams } from "../lib/bootstrap.ts";
import type {
  BoardSummary,
  ColumnPage,
  PortalBoard,
  PortalBoardBootstrap,
  PortalAttachment,
  PortalBoardFilters,
  PortalComment,
  PortalHistoryEntry,
  PortalItem,
  PortalOrderCard,
  PortalOrderResult,
  PortalPackageResult,
  PortalScope,
  PortalTodo,
  SupplierSession,
} from "../types.ts";
import type { SupplierPortalApi } from "./supplierPortal.contract.ts";

function root(scope: PortalScope): string {
  return scope.kind === "supplier"
    ? "/supplier-portal"
    : `/production/suppliers/${encodeURIComponent(scope.supplierId)}/board`;
}

function order(scope: PortalScope, supplierOrderId: string): string {
  return `${root(scope)}/orders/${encodeURIComponent(supplierOrderId)}`;
}

/** The URL form of the filters — the same one the board keeps in its own address bar. */
/** The URL's filter keys, plus the window always (so the board never relies on the server's default). */
function filterParams(filters: PortalBoardFilters): QueryParams {
  return { ...Object.fromEntries(serializeFilters(filters).entries()), window: filters.window };
}

export const supplierPortalReal: SupplierPortalApi = {
  getSession: () => apiGet<SupplierSession>("/supplier-portal/me"),

  getBoard: (scope) => apiGet<PortalBoard>(root(scope)),
  getBootstrap: (scope, params) =>
    apiGet<PortalBoardBootstrap>(`${root(scope)}/bootstrap`, bootstrapParams(params.filters, params.limit)),
  getSummary: (scope, window) => apiGet<BoardSummary>(`${root(scope)}/summary`, { window }),
  getMethods: (scope, window) => apiGet<string[]>(`${root(scope)}/methods`, { window }),
  getColumnCounts: (scope, window) => apiGet<Record<string, number>>(`${root(scope)}/column-counts`, { window }),

  getColumnPage: (scope, params) =>
    apiGet<ColumnPage>(`${root(scope)}/orders`, {
      column: params.columnId,
      limit: params.limit,
      cursor: params.cursor ?? undefined,
      ...filterParams(params.filters),
    }),

  getScheduleRange: (scope, params) =>
    apiGet<PortalOrderCard[]>(`${root(scope)}/schedule`, {
      from: params.from,
      to: params.to,
      ...filterParams(params.filters),
    }),

  getOrder: (scope, id) => apiGet<PortalOrderResult>(order(scope, id)),
  getComments: (scope, id) => apiGet<PortalComment[]>(`${order(scope, id)}/comments`),
  getHistory: (scope, id) => apiGet<PortalHistoryEntry[]>(`${order(scope, id)}/history`),

  moveToColumn: (scope, input) =>
    apiPatch<PortalOrderCard>(`${order(scope, input.supplierOrderId)}/column`, { columnId: input.columnId }),
  setWorkOn: (scope, input) =>
    apiPatch<PortalOrderCard>(`${order(scope, input.supplierOrderId)}/work-on`, { date: input.date }),
  bulkSetWorkOn: (scope, input) =>
    apiPatch<PortalOrderCard[]>(`${root(scope)}/orders/work-on`, {
      supplierOrderIds: input.supplierOrderIds,
      date: input.date,
    }),

  addTodo: (scope, input) =>
    apiPost<PortalTodo[]>(`${order(scope, input.supplierOrderId)}/todos`, { text: input.text }),
  setTodoDone: (scope, input) =>
    apiPatch<PortalTodo[]>(`${order(scope, input.supplierOrderId)}/todos/${encodeURIComponent(input.todoId)}`, {
      done: input.done,
    }),
  removeTodo: (scope, input) =>
    apiDelete<PortalTodo[]>(`${order(scope, input.supplierOrderId)}/todos/${encodeURIComponent(input.todoId)}`),

  setSupplierCode: (scope, input) =>
    apiPut<PortalItem>(
      `${order(scope, input.supplierOrderId)}/items/${encodeURIComponent(input.itemId)}/supplier-code`,
      { code: input.code },
    ),
  searchSupplierCodes: (scope, input) =>
    apiGet<string[]>(`${root(scope)}/supplier-codes`, { q: input.query }),

  uploadAttachment: (scope, input) => {
    const form = new FormData();
    form.append("file", input.file);
    return apiPostForm<PortalAttachment>(`${order(scope, input.supplierOrderId)}/attachments`, form);
  },
  postComment: (scope, input) =>
    apiPost<PortalComment>(`${order(scope, input.supplierOrderId)}/comments`, {
      body: input.body,
      attachmentIds: input.attachmentIds,
      visibility: input.visibility,
    }),

  addPackage: (scope, input) =>
    apiPost<PortalPackageResult>(`${order(scope, input.supplierOrderId)}/packages`, {
      carrier: input.carrier,
      trackingNumber: input.trackingNumber,
      lines: input.lines,
    }),
  editPackage: (scope, input) =>
    apiPatch<PortalPackageResult>(
      `${order(scope, input.supplierOrderId)}/packages/${encodeURIComponent(input.packageId)}`,
      { carrier: input.carrier, trackingNumber: input.trackingNumber, lines: input.lines },
    ),
  removePackage: (scope, input) =>
    apiDelete<PortalPackageResult>(
      `${order(scope, input.supplierOrderId)}/packages/${encodeURIComponent(input.packageId)}`,
    ),

  saveStatuses: (input) =>
    apiPut<SupplierStatus[]>(`/production/suppliers/${encodeURIComponent(input.supplierId)}/statuses`, {
      statuses: input.statuses,
      moves: input.moves,
    }),
};
